import { Controller } from "@hotwired/stimulus";
import { BarController, Chart } from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";

import ChartDataLabels from "chartjs-plugin-datalabels";

// Store reference to the current ChartController instance
let currentChartController = null;

function getImage() {
  const img = new Image();
  img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAeCAYAAAA7MK6iAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAQZSURBVHgBtVdfSFtnFP/da0isyJp069tSIlpfk75McOA6iGs6C+vefOiob/NBiL5sbjBG6bBTC7VvvtUIvg6ZUQSFzqeB0Jk+KBTzR8fSmKDmOkxMTLxfz/maFGvjzTW2B07u5eb7zu/8/c75FJigWCxmp4dHh/4NBK7Tu4vYXvpbU6CEhCJC4kgstbS0TJuRqZgA9OtC76+vr7c3NDTgQv0FWCwWqKoq1+i6jsPDQ+QP88hms8jlchuqok7QX4GmpqYNnJVimzF/JBpJJ1NJQYIFAZhiXst7aG8sHA73nAWTLX20+e+mIO1NA1ZSgGVEIpFHpkBp4ZNUKiWKxWLNoGVmGYmtBIM/qWopLzwv4ElmQ061nOPBrnkfllayvOT2/jKeUrLURUueOj91ujhjPwRx5sdfxjXKeEr2Jk3WBNXn3cbGxg8GymS1WkEYdvKAtPp1MQr0OOwOUwK4btdXQ6+3CYGzkMRQ4OfzQaXY3qbDwZS1DPTLz4P43v8DVldXJfM3swowBh9E9OpRKOBjlz6+5L/40UVTm9PpNLa3txEIBOB0Orn8MDIygqOjI9TV1VXdv/f/HnZ2dh6rQhVum9VWdQNbtbi4iK6uLszPz+N5KITe3l5sJRJQFAVra2sSvBrxkUvrv7AoQvFw4M1QoVBAW1sbvurshNfrxeDgIKw2G36iZzKVQuvVq/iR3svuZ4WYj1MppC5OLnv5wDcitoZLorW1FcPDw1heXpZWDw0N4b94HKOjozjI5SSQ78YNTE1NVZRTwrKbrp/FhQVMTk6ivb0dnmvX0N3djdDKiszyPU3D3Nwc/nn2DA9JgT9nZmCzGYeP4TXebETstr8X/sD4+Dg6OjqQ3t3F1zdvyjb4+4MHUpHm5mbMBIOYnZ2VSpwW7xKWBmpfK2a60MNfBwQJk0yxFsFgUGQzGeH3+wVTp9crvrtzR2Tom5GcTDYjotHoU5UOj6WD3IGhxaz9l75v3yQLl43P50OGLN4l6wcGBtB16xacV67gt/v3Deua84RUeK7qqj7NLjOifD4Pz2efv/WNk8ThcMh6drvd6OvrQywaxSeXLxvKklgq/pLjDU8aZ5kyTjK7n590MFQdDngykYpzp6DPj9NaGrVSuVbZAxWp5HqJIRCQwPxjUS1j+/v7Gvv/vVMJlHoyCGODQjTxBpitpq5xj4Y0VCutWkBZJk02oBH4XsXJMxwJj/GEeN6JQ8b8GCdexgXLNlSS2uQEz101j0DHAItU78nklgi/eBGAGWLteEY6T6bzocQyqlr6Dngs3MOpf46BPr0eWe8/TX61K4yrqBd7aEC7y1MKX2G4hXL/Pn6FKRQLfHUpX2FkeXKlyKStBfiEB27TVHid6sDNPRxvX9o2CGyJhoppCywhI8AyvQLTEE28X/xtKgAAAABJRU5ErkJggg==';
  return img;
}

class BarWithImage extends BarController {
  draw() {
    super.draw(arguments);

    const ctx = this.chart.ctx;
    const meta = this.getMeta();

    // If chart isn't fully rendered yet, wait for it
    if (!this.chart.chartArea || !this.chart.chartArea.width) {
      // Wait a bit and try again
      if (!this._waitingForRender) {
        this._waitingForRender = true;
        setTimeout(() => {
          this._waitingForRender = false;
          this.chart.draw();
        }, 100);
      }
      return;
    }

    // If we've already drawn the images and they're visible, don't redraw
    if (this._imagesDrawn) {
      return;
    }

    // Access the current ChartsController instance and get chart area
    const chartController = currentChartController;
    const chartArea = this.chart.chartArea;

    // Save context for drawing
    ctx.save();

    // Get default image for fallback
    const defaultImage = this._getDefaultImage(chartController);

    // Create array to hold all image loading promises
    const imagePromises = [];
    const images = [];

    // First, preload all images
    this._preloadImages(meta.data, images, imagePromises, defaultImage);

    // Wait for all images to load, then draw them
    Promise.all(imagePromises).then(() => {
      // Now draw all images
      this._drawImagesOnBars(meta.data, images, defaultImage, chartArea, ctx);

      // Mark as complete and restore context
      this._imagesDrawn = true;
      ctx.restore();
    });
  }

  // Get default image from controller or use placeholder
  _getDefaultImage(chartController) {
    const defaultImage = new Image();
    defaultImage.src = chartController?.element.dataset.chartsImageValue ||
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFeAJ5jR4YvgAAAABJRU5ErkJggg==';
    return defaultImage;
  }

  // Preload all images before drawing
  _preloadImages(bars, images, imagePromises, defaultImage) {
    bars.forEach((bar, index) => {
      try {
        const image = new Image();
        images[index] = image;

        const promise = new Promise(resolve => {
          image.onload = () => resolve();
          image.onerror = () => resolve(); // Continue even if image fails to load

          // Set image source
          if (bar.$context.raw?.nested?.image) {
            // Make sure the URL is absolute
            let imageUrl = bar.$context.raw.nested.image;

            // If the URL is relative, convert it to absolute
            if (imageUrl.startsWith('/')) {
              imageUrl = window.location.origin + imageUrl;
            }

            image.src = imageUrl;
          } else {
            image.src = defaultImage.src;
          }
        });

        imagePromises.push(promise);
      } catch (error) {
        // Silently continue if image loading fails
      }
    });
  }

  // Draw all images on their respective bars
  _drawImagesOnBars(bars, images, defaultImage, chartArea, ctx) {
    const imageWidth = 32;
    const imageHeight = 32;

    bars.forEach((bar, index) => {
      try {
        // Get the X and Y position of the bar
        const { x, y, base } = bar.getProps(["x", "y", "base"]);

        // Get the value for this bar
        const barValue = bar.$context?.raw?.nested?.value || 0;

        // Calculate bar height
        const barHeight = Math.abs(base - y);

        // Use the X position of the bar
        const imageX = x - (imageWidth / 2);

        // Calculate Y position based on bar value
        let imageY;

        if (barValue === 0 || barHeight < 5) {
          // For zero value, position at bottom of chart to match the "0" value
          imageY = chartArea.bottom - (imageHeight / 2); // Half the image overlaps with base line
        } else {
          // Position image with its center at the top of the bar
          imageY = y - (imageHeight / 2);
        }

        // Draw the image with shadow effect
        drawImageWithShadow(ctx, images[index] || defaultImage, imageX, imageY, imageWidth, imageHeight);

      } catch (error) {
        // Silently continue if drawing fails
      }
    });
  }
}

// Helper function to draw image with shadow
function drawImageWithShadow(ctx, image, x, y, width, height) {
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
  ctx.shadowBlur = 10;
  ctx.shadowOffsetX = 4;
  ctx.shadowOffsetY = 4;
  ctx.drawImage(image, x, y, width, height);
  ctx.restore();
}

BarWithImage.id = "barWithImage";
BarWithImage.defaults = BarController.defaults;
Chart.register(annotationPlugin);
Chart.register(BarWithImage);
Chart.register(ChartDataLabels);

const START_TAB = {
  targets: "month",
  kpi: "day",
  weight: "days",
  waist: "days",
};

export class ChartsController extends Controller {
  static targets = ["canvas", "currentText", "totalText"];
  static values = {
    type: { type: String, default: "line" },
    chartType: { type: String, default: "line" },
    endpointOptions: String,
    endpointData: String,
    data: Object,
    options: Object,
    chartDate: String
  };

  async fetchChartData() {
    const [optionsUrl, dataUrl] =
      [this.endpointOptionsValue, this.endpointDataValue] || [];
    if (!optionsUrl || !dataUrl) return {};

    const [options, data] = await Promise.all([
      fetch(optionsUrl).then((response) => response.json()),
      fetch(dataUrl).then((response) => response.json()),
    ]);

    return { options, data };
  }

  async connect() {
    // Store reference to this controller instance
    currentChartController = this;

    const { options, data } = await this.fetchChartData();

    this.originalData = JSON.parse(JSON.stringify(data));
    this.currentPeriod = START_TAB[this.typeValue];

    const ctx = this.canvasTarget.getContext("2d");
    // Make sure we're using barWithImage for the right chart type
    let chartType = this.chartTypeValue;
    const config = this.getChartConfig(
      chartType,
      { datasets: [] },
      options,
      this.currentPeriod
    );

    this.chart = new Chart(ctx, config);
    this.updateButtons = this.element.querySelectorAll(
      "[data-action='click->charts#update']"
    );

    this.updateActiveButton();
    this.updateChartWithCurrentPeriod();

    // Store the chart date for KPI doughnut charts - use data attribute if available
    if (this.chartTypeValue === "doughnut" && this.typeValue === "kpi") {
      this.chartDate = this.chartDateValue || this.getChartDateFromEndpoint();
    }

    // Store bound function reference for proper cleanup
    this.boundHandleGoalCompletionUpdate = this.handleGoalCompletionUpdate.bind(this);

    // Listen for goal completion updates
    document.addEventListener('goal-completion:updated', this.boundHandleGoalCompletionUpdate);
  }

  disconnect() {
    // Remove event listener when controller disconnects
    document.removeEventListener('goal-completion:updated', this.boundHandleGoalCompletionUpdate);

    this.chart.destroy();
    this.chart = undefined;
  }

  // Extract date from the endpoint URL for comparison
  getChartDateFromEndpoint() {
    try {
      const url = new URL(this.endpointDataValue, window.location.origin);
      return url.searchParams.get('date');
    } catch (e) {
      console.error("Error parsing chart date:", e);
      return null;
    }
  }

  // Handle goal completion updates
  async handleGoalCompletionUpdate(event) {
    // Only refresh data if this is a KPI doughnut chart AND matches the completion date
    if (this.chartTypeValue === "doughnut" && this.typeValue === "kpi") {
      // Check if event has date information and if it matches this chart's date
      const completionDate = event.detail?.date;
      const chartDate = this.chartDate;

      // If no date in event or they match (or chartDate is null), refresh data
      if (!completionDate || !chartDate || completionDate === chartDate) {
        await this.refreshData();
        this.updateChartWithCurrentPeriod();
      }
    }
  }

  // Fetch new data and update originalData
  async refreshData() {
    try {
      const { data } = await this.fetchChartData();
      if (data && data.datasets && data.datasets.length > 0) {
        // Update the complete original data
        this.originalData = data;
        return true;
      }
    } catch (error) {
      console.error("Error refreshing chart data:", error);
    }
    return false;
  }

  // Updates the chart based on the current period
  async updateChartWithCurrentPeriod() {
    if (!this.originalData?.datasets) return;
    // Find the dataset for the current period
    const dataset = this.originalData.datasets.find(
      (ds) => ds.label === this.currentPeriod
    ) || this.originalData.datasets[0];

    // More efficiently update the chart's dataset
    if (this.chart.data.datasets.length === 0) {
      this.chart.data.datasets.push(dataset);
    } else {
      this.chart.data.datasets[0] = dataset;
    }

    // Update labels if needed
    if (this.originalData.labels) {
      this.chart.data.labels = this.originalData.labels;
    }

    // Then update based on chart type
    if (this.chartTypeValue === "line") {
      this.updateLineChartGradient();
      this.chart.update();
    } else if (this.chartTypeValue === "doughnut") {
      // For doughnut charts, update text and chart
      try {
        // Calculate values for text displays
        const data = this.chart.data.datasets[0]?.data;
        if (data) {
          const currentValue = data[0] || 0;
          const totalValue = data.reduce((a, b) => a + b, 0);

          // Update text displays if targets exist
          if (this.currentTextTarget) {
            this.currentTextTarget.textContent = currentValue;
          }

          if (this.totalTextTarget) {
            this.totalTextTarget.textContent = totalValue;
          }
        }
      } catch (e) {
        console.error("Error updating text values:", e);
      }

      // Update the chart
      this.chart.update();
    } else {
      this.chart.update();
    }
  }

  update(event) {
    this.currentPeriod = event.currentTarget.dataset.period;
    this.updateActiveButton();
    this.updateChartWithCurrentPeriod();
  }

  updateActiveButton() {
    this.updateButtons.forEach((button) => {
      button.classList.toggle(
        "active",
        button.dataset.period === this.currentPeriod
      );
    });
  }

  updateLineChartGradient() {
    this.chart.data.datasets.forEach((dataset) => {
      if (dataset.label === this.currentPeriod) {
        dataset.backgroundColor = this.getGradient();
      }
    });
  }

  getGradient() {
    const ctx = this.canvasTarget.getContext("2d");
    const gradient = ctx.createLinearGradient(
      0,
      0,
      0,
      this.canvasTarget.height
    );

    gradient.addColorStop(0, "rgba(197, 26, 33, 0.35)");
    gradient.addColorStop(0.55, "rgba(255, 255, 255, 0)");

    return gradient;
  }

  getChartConfig(chartType, data, options, period) {
    if (chartType === "line") {
      return {
        type: chartType,
        options: {
          ...options,
          plugins: {
            ...options.plugins,
            annotation: {
              ...options.plugins?.annotation,
              annotations: {
                ...options.plugins?.annotation?.annotations,
                targetLine: {
                  ...options.plugins?.annotation?.annotations?.targetLine,
                  type: 'line',
                  borderColor: 'rgba(32, 39, 32, 0.15)',
                  backgroundColor: 'white',
                  borderWidth: 1,
                },
                label: {
                  ...options.plugins?.annotation?.annotations?.label,
                  content: getImage(),
                  display: true,
                  position: 'center',
                  backgroundColor: 'transparent',
                  borderWidth: 0,
                },
              }
            },
            datalabels: {
              ...options.plugins?.datalabels,
              formatter: function (value) {
                return value.y;
              },
              display: function (context) {
                return context.dataIndex % 5 === 0;
              },
            },
          },
        },
        data,
      };
    }

    if (chartType === "barWithImage") {
      return {
        type: chartType,
        options: {
          ...options,
          layout: {
            ...(options.layout || {}),
            padding: {
              ...(options.layout?.padding || {}),
              top: 40, // Add extra padding at the top for images
            }
          },
          // Disable animations to prevent excessive redraws
          animation: {
            duration: 0 // general animation time
          },
          hover: {
            animationDuration: 0 // duration of animations when hovering an item
          },
          responsiveAnimationDuration: 0, // animation duration after a resize
          plugins: {
            ...options.plugins
          }
        },
        data,
      };
    }

    // besides weight chart everything can be configured with options from the backend
    return {
      type: chartType,
      options,
      data,
    };
  }

  // For backward compatibility
  updateChart() {
    this.updateChartWithCurrentPeriod();
  }
}

export default ChartsController;
